/**
 * TipJar Component — Vanilla DOM Injection
 * 
 * Simple tip jar linking to https://tiptopjar.com/chromedinogame
 * Designed for runtime DOM injection into SystemMenu.
 * Runs after React hydration to avoid conflicts with React's virtual DOM.
 */

export interface TipJarConfig {
  tipJarUrl?: string; // Default: https://tiptopjar.com/chromedinogame
}

/**
 * Creates the TipJar DOM element with vanilla markup and event listeners.
 * Returns the root element ready for appendChild.
 */
export function createTipJarElement(config: TipJarConfig = {}): HTMLDivElement {
  const tipJarUrl = config.tipJarUrl || 'https://tiptopjar.com/chromedinogame';
  
  const container = document.createElement('div');
  container.id = 'tip-jar-container';
  container.setAttribute('data-component', 'tip-jar');

  // Inline styles to ensure isolation from global CSS
  container.style.cssText = `
    position: absolute;
    bottom: 16px;
    right: 16px;
    z-index: 1000;
    width: 280px;
    background: var(--card-bg, #ffffff);
    border: 1px solid var(--chrome-border, #d3d3d3);
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    padding: 16px;
    font-family: system-ui, -apple-system, Roboto, Arial, sans-serif;
    font-size: 13px;
    color: var(--text-color, #202124);
  `;

  // Header
  const header = document.createElement('div');
  header.style.cssText = `
    margin-bottom: 12px;
    display: flex;
    align-items: center;
    gap: 8px;
  `;
  const icon = document.createElement('span');
  icon.textContent = '🫙';
  icon.style.fontSize = '18px';
  const title = document.createElement('h3');
  title.textContent = 'Support Us';
  title.style.cssText = `
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-color, #202124);
  `;
  header.appendChild(icon);
  header.appendChild(title);

  // Description
  const description = document.createElement('p');
  description.textContent = 'Enjoying the game? Leave a tip to help us keep it running.';
  description.style.cssText = `
    margin: 0 0 12px 0;
    font-size: 12px;
    color: var(--text-secondary, #5f6368);
    line-height: 1.4;
  `;

  // Support button
  const button = document.createElement('button');
  button.textContent = '💝 Leave a Tip';
  button.style.cssText = `
    width: 100%;
    padding: 10px 16px;
    background: var(--accent, #FF6B35);
    color: #ffffff;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 600;
    transition: background 0.2s ease, transform 0.15s ease;
    font-family: inherit;
    margin-bottom: 12px;
  `;

  button.addEventListener('mouseenter', () => {
    button.style.background = 'var(--accent-hover, #e55a24)';
  });
  button.addEventListener('mouseleave', () => {
    button.style.background = 'var(--accent, #FF6B35)';
  });
  button.addEventListener('mousedown', () => {
    button.style.transform = 'scale(0.97)';
  });
  button.addEventListener('mouseup', () => {
    button.style.transform = 'scale(1)';
  });

  button.addEventListener('click', (e) => {
    e.stopPropagation();
    window.open(tipJarUrl, '_blank', 'noopener,noreferrer');
    console.log(`[TipJar] Opened: ${tipJarUrl}`);
  });

  // Footer note
  const footer = document.createElement('p');
  footer.textContent = 'All tips support server costs and development.';
  footer.style.cssText = `
    margin: 0;
    font-size: 11px;
    color: var(--text-secondary, #5f6368);
    text-align: center;
    font-style: italic;
  `;

  // Assemble
  container.appendChild(header);
  container.appendChild(description);
  container.appendChild(button);
  container.appendChild(footer);

  return container;
}

/**
 * Inject TipJar into the SystemMenu footer.
 * Safe to call multiple times; checks for existing container first.
 */
export function injectTipJar(config: TipJarConfig = {}): boolean {
  try {
    // Check if already injected
    if (document.getElementById('tip-jar-container')) {
      console.log('[TipJar] Already injected, skipping');
      return true;
    }

    // Find the SystemMenu modal content
    const modalContent = document.querySelector('[class*="modalContent"]');
    if (!modalContent) {
      return false;
    }

    // Create and inject the TipJar element
    const tipJarElement = createTipJarElement(config);
    modalContent.appendChild(tipJarElement);

    console.log('[TipJar] Successfully injected into SystemMenu');
    return true;
  } catch (error) {
    console.error('[TipJar] Injection failed:', error);
    return false;
  }
}
